import type { Hook } from "./types";
import type { TagVariant } from "../../components/ui/Tag";
import type { ReadinessReason } from "./hooks/useAgentEngine";

// ─── HooksPanel helpers ──────────────────────────────────────────────

export function groupHooksByScope(hooks: readonly Hook[]): Record<string, Hook[]> {
  const groups: Record<string, Hook[]> = {};
  for (const hook of hooks) {
    const scope = hook.trigger_scope ?? "general";
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (!groups[scope]) groups[scope] = [];
    groups[scope].push(hook);
  }
  return groups;
}

// ─── IdentityPanel helpers ───────────────────────────────────────────

const TAG_COLORS: Readonly<Record<string, TagVariant>> = {
  Friend: "orange",
  Partner: "blue",
  Crypto: "green",
  "Berlin Tech": "purple",
};

export function getTagColor(label: string): TagVariant {
  return TAG_COLORS[label] ?? "default";
}

export function formatRelativeDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks === 1) return "1 week ago";
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    if (days < 30) return `${weeks} weeks ago`;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${Math.floor(days / 30)} months ago`;
  } catch {
    return "recently";
  }
}

// ─── AllowListPanel helpers ──────────────────────────────────────────

export function normalizeTargetType(type: string): string {
  if (type === "telegram" || type === "telegram_chat") return "telegram";
  if (type === "email" || type === "email_address" || type === "gmail") return "email";
  return "general";
}

export function getGroupLabel(type: string): string {
  if (type === "telegram") return "TELEGRAM";
  if (type === "email") return "EMAIL";
  return "GENERAL";
}

// ─── Agent readiness ─────────────────────────────────────────────────

/**
 * Short pill text for an engine-readiness verdict.
 *
 * The wording says what is WRONG, not merely that something is: "not signed
 * in" and "not installed" send a person to different places, and collapsing
 * both into "unavailable" is what made the agent list feel arbitrary.
 */
export function readinessLabel(reason: ReadinessReason | undefined): string {
  switch (reason) {
    case "ok":
      return "ready";
    case "binary_missing":
      return "not installed";
    case "not_signed_in":
      return "not signed in";
    case "node_missing":
      return "needs Node.js";
    case "needs_model":
      return "needs a model";
    case "unknown":
    case undefined:
      return "unavailable";
  }
}
